import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { Flex, Button, Layout, Menu } from 'antd';
import { SettingFilled } from '@ant-design/icons';

import Garland from '@core/controls/easter-eggs/garland/garland';

import { isNewYearAroundUs } from '@extensions/utils';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { setCurrentMenuItem, userLoaded } from '@store/actions';
import { serverFetch } from '@src/server';

import { IUserSession } from '@entities/user-session';
import { Permission, hasPermission } from '@enums/permission';

import {
    WarehouseIcon,
    AcceptanceIcon,
    LogisticsIcon,
    ImportTruckIcon,
    DeliveryTruckIcon,
    CartIcon,
    BoxesIcon,
    WorldTruckIcon,
    MiniLogoIcon,
    MainLogoIcon,
    UsdIcon,
    AccountingIcon,
    BillIcon,
} from '@icons/index';

import '@core/index.css';

const getDefaultOpenKey = (menuItem: string) => {
    switch (menuItem) {
        case 'import':
        case 'transit':
        case 'delivery':
            return 'logistics';

        case 'acceptance':
        case 'remainings':
        case 'shipmentjournals':
            return 'warehouse';

        case 'balances':
            return 'finances';

        case 'consignees':
        case 'users':
            return 'settings';

        default:
            return '';
    }
};
interface IMainLayout {
    appVersion: string | undefined;
}

const MainLayout = (props: IMainLayout) => {
    const { appVersion } = props;

    const warehouseCode = process.env.REACT_APP_WHS_CODE;
    const warehouseName = process.env.REACT_APP_WHS_NAME;

    const { Header, Content, Sider } = Layout;

    const location = useLocation();

    const d = useAppDispatch();
    const currentMenuItem = useAppSelector<string>((s) => s.currentMenuItem);
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [defaultOpenKey, setDefaultOpenKey] = useState(getDefaultOpenKey(currentMenuItem));

    const [viewSetup] = useState<boolean>(hasPermission(userSession.permissions, Permission.ViewSetup));

    const [siderCollapsed, setSiderCollapsed] = useState<boolean>(false);

    function getItem(label: any, key: string, urlTo?: string, icon?: any, children?: Array<any>) {
        const itemlabel = urlTo ? <Link to={urlTo}>{label}</Link> : label;

        return {
            key,
            icon,
            children,
            label: itemlabel,
        };
    }

    useEffect(() => {
        const items = location.pathname.split('/');

        let key;
        switch (items[1]) {
            case 'logistics':
            case 'finances':
            case 'warehouse':
            case 'settings':
                key = items[2];
                break;
            default:
                key = items[1];
        }

        d(setCurrentMenuItem(key));
    }, []);

    const onLogout = () => {
        serverFetch(`auth/logout`, { method: 'POST' }).then(() => {
            d(userLoaded(undefined));
        });
    };

    useEffect(() => {
        setDefaultOpenKey(getDefaultOpenKey(currentMenuItem));
    }, [currentMenuItem]);

    const items = [
        getItem('Логистика', 'logistics', undefined, <LogisticsIcon />, [
            getItem('Импорт', 'import', '/logistics/import', <WorldTruckIcon />),
            getItem('Транзит', 'transit', '/logistics/transit', <ImportTruckIcon />),
            getItem('Доставка', 'delivery', '/logistics/delivery', <DeliveryTruckIcon />),
        ]),
        getItem('Склад', 'warehouse', undefined, <WarehouseIcon />, [
            getItem('Приемка', 'acceptance', '/warehouse/acceptance', <AcceptanceIcon />),
            getItem('Остатки', 'remainings', '/warehouse/remainings', <BoxesIcon />),
            getItem('Отгрузка', 'shipmentjournals', '/warehouse/shipmentjournals', <CartIcon />),
        ]),
    ];

    if (
        hasPermission(userSession.permissions, Permission.ViewTransportBill) ||
        hasPermission(userSession.permissions, Permission.ViewBalance)
    ) {
        const finItems = [];

        if (hasPermission(userSession.permissions, Permission.ViewTransportBill)) {
            finItems.push(getItem('Счета на оплату', 'bills', '/finance/bills', <BillIcon />));
        }

        if (hasPermission(userSession.permissions, Permission.ViewBalance)) {
            finItems.push(getItem('Балансы', 'balances', '/finance/balances', <AccountingIcon style={{ fontSize: 22 }} />));
        }

        items.push(getItem('Финансы', 'finances', undefined, <UsdIcon style={{ fontSize: 16 }} />, finItems));
    }

    if (viewSetup) {
        items.push(
            getItem('Управление', 'settings', undefined, <SettingFilled />, [
                getItem('Грузополучатели', 'consignees', '/settings/consignees'),
                getItem('Пользователи', 'users', '/settings/users'),
            ]),
        );
    }

    const onMenuClick = (e: any) => {
        d(setCurrentMenuItem(e.key));
    };

    const userMenuItems = [
        getItem(
            <div className='user' style={{ cursor: 'pointer', textAlign: 'center', minWidth: 200 }}>
                {userSession.fullName}
            </div>,
            'account',
            undefined,
            undefined,
            [getItem('Личный кабинет', 'useraccount', '/useraccount', undefined)],
        ),
    ];

    const getLogo = () => {
        return siderCollapsed ? (
            <div className='logo'>
                <div className='sub-logo'>{warehouseCode}</div>
                <MiniLogoIcon style={{ fontSize: 30, display: 'table-cell', verticalAlign: 'middle' }} />
            </div>
        ) : (
            <div className='logo'>
                <div className='sub-logo'>{warehouseName}</div>
                <MainLogoIcon style={{ fontSize: 46, display: 'table-cell', verticalAlign: 'middle' }} />
            </div>
        );
    };

    return (
        <Layout style={{ height: '100vh', minHeight: '100vh' }}>
            <Sider
                collapsible={true}
                onCollapse={(e) => setSiderCollapsed(e)}
                width={250}
                style={{
                    overflow: 'auto',
                    height: '100vh',
                    zIndex: 100,
                }}
            >
                {getLogo()}
                <Flex style={{ height: 'calc(100vh - 120px)' }} vertical justify='space-between'>
                    <Menu
                        defaultOpenKeys={[defaultOpenKey]}
                        defaultSelectedKeys={[currentMenuItem]}
                        mode='inline'
                        items={items}
                        onClick={onMenuClick}
                    />
                    <div className='app-version'>Версия {appVersion}</div>
                </Flex>
            </Sider>
            <Layout>
                <Header className='main-header'>
                    <Flex align='center' justify='flex-end' gap='small'>
                        {isNewYearAroundUs() && <Garland />}
                        <Flex align='center' style={{ background: '#ffffff', position: 'relative' }}>
                            <Menu items={userMenuItems} mode='horizontal' />
                              <Button variant='link' color='blue' className='logout' onClick={() => onLogout()}>
                                Выход
                            </Button>
                        </Flex>
                    </Flex>
                </Header>
                <Content className='main-content'>
                    <Outlet />
                </Content>
            </Layout>
        </Layout>
    );
};

export default MainLayout;
